import { Table, TableBody } from '@mdvip/shared/components/ui/table';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';

interface PaymentsTableShellProps {
  title: string;
  description: string;
  /** Overrides the default rail typography (`mt-2 text-sm`) for callers with different disclaimer sizing. */
  descriptionClassName?: string;
  /** Rendered under the description in the left rail — a "view all" link or an action button. */
  headerAction?: ReactNode;
  /** The caller resolves loading/error/empty precedence before handing this down. */
  state: 'error' | 'loading' | 'empty' | 'loaded';
  /** Rendered in place of the table when `state` is `'error'`, so each consumer owns its own emphasis. */
  errorContent?: ReactNode;
  emptyState?: ReactNode;
  /** Rendered above the table card, e.g. a result count line. Loaded state only. */
  aboveTable?: ReactNode;
  /**
   * Rendered at the top of the right-hand region in every state, so it stays put when the table
   * flips to its empty or error slot. Use this for a notice that has to outlive the rows it refers
   * to, e.g. a confirmation of the row that was just removed; use `aboveTable` for anything that
   * only makes sense alongside actual rows.
   */
  notice?: ReactNode;
  tableHeader: ReactNode;
  /** Body content while `state` is `'loading'`. */
  skeleton: ReactNode;
  /** Body content once loaded and non-empty. */
  rows: ReactNode;
  /** Rendered below the table card, e.g. a pager. Loaded state only. */
  footer?: ReactNode;
  className?: string;
}

/**
 * The shell shared by every members-app payments table: a left rail (title, description, optional
 * action) beside a right-hand region that switches between an error slot, an empty-state slot, and
 * the loaded table card. Column headers, skeleton rows, data rows, and any footer are supplied by
 * the caller — this component owns only the layout and the error/loading/empty/loaded branching,
 * not the row shape, which differs enough between callers (pagination, row actions) to stay local.
 *
 * `state` is a single 4-way union rather than a status plus a separate `isEmpty` flag: each caller
 * resolves precedence (error beats loading beats empty) before it reaches this component, so there's
 * no ordering to get wrong here.
 */
export function PaymentsTableShell(props: PaymentsTableShellProps) {
  const {
    title,
    description,
    descriptionClassName,
    headerAction,
    state,
    errorContent,
    emptyState,
    aboveTable,
    notice,
    tableHeader,
    skeleton,
    rows,
    footer,
    className,
  } = props;

  const isLoading = state === 'loading';
  const isError = state === 'error';
  const showEmpty = state === 'empty';

  return (
    <div
      className={cn('grid min-w-0 grid-cols-4 gap-4 md:grid-cols-8 md:gap-6 lg:grid-cols-12 lg:gap-6', className)}
      data-slot="payments-table-shell"
    >
      <div className="col-span-4 flex flex-col md:col-span-8 lg:col-span-4">
        <h3 className="text-h3 font-semibold text-foreground">{title}</h3>
        <p className={cn('leading-relaxed text-muted-foreground', descriptionClassName ?? 'mt-2 text-sm')}>
          {description}
        </p>
        {headerAction}
      </div>

      <div className="col-span-4 min-w-0 md:col-span-8 lg:col-span-8">
        {notice}
        {isError ? (
          errorContent
        ) : showEmpty ? (
          emptyState
        ) : (
          <>
            {aboveTable}
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <Table className="sm:table-fixed" aria-busy={isLoading || undefined}>
                {tableHeader}
                <TableBody>{isLoading ? skeleton : rows}</TableBody>
              </Table>
            </div>
            {footer}
          </>
        )}
      </div>
    </div>
  );
}
